import { useCallback, useContext } from 'react';
import { Column } from 'react-rainbow-components';
import { showAppNotification } from '@rainbow-modules/app';
import context from 'components/InstructionTestingLayout/context';
import Button from 'components/Button';
import Table from 'components/Table';
import useHttpMutation from 'data/firestore/useHttpMutation';
import toTestConfig from '../../../helpers/toTestConfig';
import { Container, Footer } from '../styled';
import { ResponderInstructionTestingContext, ResponderInstructionTestStepComponentProps } from '../../types';
import RoleColumn from './columns/role';
import NewContentColumn from './columns/newContent';
import OriginalContentColumn from './columns/original';

const LLmConversationReplayOutputView = ({
    agentId,
    responderId,
    systemMessageTemplate,
    onChange = () => {},
}: ResponderInstructionTestStepComponentProps) => {
    const {
        responderConfig,
        chatMessages,
        templateContext: input,
        output,
    } = useContext<ResponderInstructionTestingContext>(context);

    const {
        mutateAsync: replayConversation,
        isLoading,
    } = useHttpMutation<Record<string, unknown>, Record<string, unknown>>({
        pathname: `/agents/${agentId}/llms/${responderId}/test/replay-conversation`,
        method: 'POST',
        server: process.env.REACT_APP_BASE_URL,
    });

    const handleReplay = useCallback(
        async () => {
            onChange({ output: undefined });
            try {
                const response = await replayConversation({
                    body: {
                        input,
                        config: toTestConfig(responderConfig),
                        messages: chatMessages,
                        template: systemMessageTemplate,
                    },
                });
                onChange({ output: response });
            } catch (error) {
                const message = (error as any)?.message || 'Something went wrong. Please try again.';
                showAppNotification({
                    title: 'Error',
                    description: message,
                    icon: 'error',
                    timeout: 5000,
                });
            }
        },
        [responderConfig, chatMessages, input, replayConversation, onChange, systemMessageTemplate],
    );

    const data = isLoading ? [] : output?.content as Record<string, unknown>[];

    return (
        <Container>
            <Table
                keyField="id"
                data={data}
                isLoading={isLoading}
            >
                <Column field="role" width={120} component={RoleColumn} />
                <Column header="Original Response" field="content.original" component={OriginalContentColumn} />
                <Column header="New Response" field="content.new" component={NewContentColumn} fallbackValue="(Same)" />
            </Table>
            <Footer align="flex-end">
                <Button
                    variant="brand"
                    borderRadius="semi-square"
                    label="Replay"
                    disabled={isLoading}
                    onClick={handleReplay}
                />
            </Footer>
        </Container>
    );
};

export default LLmConversationReplayOutputView;
